// Trusstor's issues through the backend, and the site catalogs the issue form
// picks from. Web and mobile bind this to their own transport.

import type { IConfigArea, IIssue, IProfession, IUser } from '../../trusstor/index.js';
import { parseStandardErrors } from './monitor.js';
import type { AuthedFetch } from './transport.js';

/** Where an issue attachment's bytes are read — an image comment's `content.value` is its id. */
export function issueAttachmentPath(siteId: string, issueId: string, attachmentId: string): string {
  return `/api/sites/${encodeURIComponent(siteId)}/issues/${encodeURIComponent(issueId)}/attachments/${encodeURIComponent(attachmentId)}`;
}

export function createIssuesApi(authedFetch: AuthedFetch) {
  const site = (siteId: string) => `/api/sites/${encodeURIComponent(siteId)}`;
  const issue = (siteId: string, issueId: string) => `${site(siteId)}/issues/${encodeURIComponent(issueId)}`;

  async function json<T>(res: Response): Promise<T> {
    if (res.ok) return (await res.json()) as T;
    return parseStandardErrors(res);
  }

  const send = (method: 'POST' | 'PUT', body: unknown): RequestInit => ({
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  return {
    fetchSiteIssues: async (siteId: string, signal?: AbortSignal) =>
      json<{ issues: IIssue[] }>(await authedFetch(`${site(siteId)}/issues`, { signal })),

    fetchSiteProfessions: async (siteId: string, signal?: AbortSignal) =>
      json<{ professions: IProfession[] }>(await authedFetch(`${site(siteId)}/professions`, { signal })),

    fetchSiteUsers: async (siteId: string, signal?: AbortSignal) =>
      json<{ users: IUser[] }>(await authedFetch(`${site(siteId)}/users`, { signal })),

    fetchAreas: async (siteId: string, signal?: AbortSignal) =>
      json<{ areas: IConfigArea[] }>(await authedFetch(`${site(siteId)}/areas`, { signal })),

    createIssueApi: async (siteId: string, body: Partial<IIssue>) =>
      json<IIssue>(await authedFetch(`${site(siteId)}/issues`, send('POST', body))),

    updateIssueApi: async (siteId: string, issueId: string, patch: Partial<IIssue>) =>
      json<IIssue>(await authedFetch(issue(siteId, issueId), send('PUT', patch))),

    deleteIssueApi: async (siteId: string, issueId: string): Promise<void> => {
      const res = await authedFetch(issue(siteId, issueId), { method: 'DELETE' });
      if (!res.ok) return parseStandardErrors(res);
    },

    addTextCommentApi: async (siteId: string, issueId: string, value: string) =>
      json<IIssue>(await authedFetch(`${issue(siteId, issueId)}/comments/text`, send('POST', { value }))),

    /**
     * Upload an image as a comment on an issue; returns the issue with it
     * appended. The app builds the multipart form — a `File` part on the web,
     * a native file part on mobile — under the field `file`. No Content-Type:
     * the transport leaves a `FormData` body's multipart boundary to fetch.
     */
    addImageCommentApi: async (siteId: string, issueId: string, form: FormData) =>
      json<IIssue>(await authedFetch(`${issue(siteId, issueId)}/comments/file`, { method: 'POST', body: form })),

    /** An issue attachment's bytes (the backend streams them from Trusstor). */
    fetchAttachmentBlob: async (siteId: string, issueId: string, attachmentId: string): Promise<Blob> => {
      const res = await authedFetch(issueAttachmentPath(siteId, issueId, attachmentId));
      if (res.ok) return res.blob();
      return parseStandardErrors(res);
    },
  };
}
